import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  ChangeDetectionStrategy,
  AfterViewInit,
  NgZone,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { of, Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { isSignedUrlExpired } from '@app/core/aws/s3';
import { AuthService } from '@app/core/auth/auth';
import { SessionStorageService } from '@app/core/auth/session-storage';
import { BreadcrumbComponent } from '@app/shared/components/breadcrumb/breadcrumb';
import { IconComponent } from '@app/shared/components/icon/icon';
import { SkeletonComponent } from '@app/shared/components/skeleton/skeleton';
import { SpinnerComponent } from '@app/shared/components/spinner/spinner';
import { FetchState } from '@app/shared/models/fetch-state.model';
import { ReviewService } from '@app/features/hospital/reviews/reviews';
import { Review, ReviewTarget } from '@app/shared/models/review.model';
import { formatId } from '@app/shared/utils/id-formats/id-formats';
import { WindowRef } from '@app/core/providers/window-ref';

@Component({
  selector: 'app-data-types',
  imports: [
    CommonModule,
    RouterLink,
    BreadcrumbComponent,
    IconComponent,
    SkeletonComponent,
    SpinnerComponent,
  ],
  templateUrl: './data-types.html',
  styleUrls: ['./data-types.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataTypesComponent implements OnInit, AfterViewInit {
  reviewId = '';
  reviewState$: Observable<FetchState<Review[]>> = of({ status: 'loading' });
  targets$: Observable<ReviewTarget[]> = of([]);
  bulkDownloadAddress = '';
  targetHospitalCode: string | null = null;
  targetHospitalName: string | null = null;

  @ViewChild('tableContainer') tableContainerRef?: ElementRef<HTMLDivElement>;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly reviewService: ReviewService,
    private readonly snackBar: MatSnackBar,
    private readonly ngZone: NgZone,
    private readonly authService: AuthService,
    private readonly sessionService: SessionStorageService,
    private readonly windowRef: WindowRef,
  ) {}

  ngAfterViewInit(): void {
    this.restoreScrollPosition();
  }

  ngOnInit(): void {
    this.reviewId = this.route.snapshot.paramMap.get('id') ?? '';

    if (this.authService.isAdminUser()) {
      this.targetHospitalCode = this.sessionService.getTargetHospitalCode();
      this.targetHospitalName = this.sessionService.getTargetHospitalName();
      if (!this.targetHospitalCode || !this.targetHospitalName) {
        this.router.navigate(['admin', 'reviews']);
      }
    }

    this.initializeReviewState();
    this.initializeTargets();
  }

  initializeReviewState(): void {
    if (this.reviewService.isInitialLoad()) {
      this.reviewService.fetchReviews(this.targetHospitalCode);
    }
    this.reviewState$ = this.reviewService.reviewState$;
  }

  initializeTargets(): void {
    this.targets$ = this.reviewState$.pipe(
      map((state) => {
        if (state.status === 'success' && state.data) {
          const matchedReview = state.data.find(
            (review) => formatId(review.id) === this.reviewId, // urlに含まれるreviewidはxxx-xxxxの形式
          );
          if (!matchedReview || matchedReview.status !== 'processing') {
            this.router.navigate(['hospital', 'reviews']); // 直接urlを入力された場合への対策（reviewidが存在しない または レビュー中以外）
            return [];
          }
          this.bulkDownloadAddress = matchedReview.address;
          return matchedReview.targets ?? [];
        }
        return [];
      }),
    );
  }

  //ページ遷移する際に選択されたデータ種のスクロール位置をキャッシュする
  saveScrollPosition(): void {
    const tableContainer = this.tableContainerRef?.nativeElement;
    if (!tableContainer) return;
    this.sessionService.savePositionDataType(
      tableContainer.scrollTop.toString(),
    );
  }

  // パンくずや戻るボタンで本画面に戻った際に、saveScrollPosition()でキャッシュしたスクロール位置に移動する
  restoreScrollPosition(): void {
    const tableContainer = this.tableContainerRef?.nativeElement;
    const scrollPosition = this.sessionService.getPositionDataType();
    if (!tableContainer || !scrollPosition) return;

    this.ngZone.onStable.pipe(take(1)).subscribe(() => {
      tableContainer.scrollTop = +scrollPosition;
      this.sessionService.removePositionDataType();
    });
  }

  // 署名付きURlの期限切れチェックと画面上に署名付きURLを表示させないために直接<a>で定義せず下記のようにしている
  onDownload(presignedUrl: string): void {
    if (isSignedUrlExpired(presignedUrl)) {
      // 署名付きURLの期限が切れていた場合はトーストを表示してリロードする
      const snackBarRef = this.snackBar.open(
        'ダウンロードリンクの有効期限が切れたため、ファイルをダウンロードできません。\nページを再読み込みしてから、再度ダウンロードをお試しください。',
        '×',
        {
          duration: 2500,
          panelClass: 'error-toast',
          verticalPosition: 'top',
          horizontalPosition: 'center',
        },
      );
      // トーストを手動で消すか時間経過で消えたらリロード実行
      snackBarRef.afterDismissed().subscribe(() => {
        this.windowRef.reload();
      });
      return;
    }

    const link = document.createElement('a');
    link.href = presignedUrl;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
